// Monetisation → Web payments (contracts/web.md §5.7): Pro on iqdaily.app/play through Stripe Checkout. A
// connection card per mode (keys, account, webhook), the restricted-key permissions, the payments.stripe
// settings (active mode with a confirm step, the master switch, checkout options, grace), then Packages,
// Transactions and Webhook events for a chosen mode. Editors read; admins change; viewers don't see it.

import { useId, useState } from "react";
import { useConnectStripe, useDisconnectStripe, useStripeSettings, useUpdateStripeSettings } from "../../api/payments";
import type { StripeConn, StripeSettings, StripeSettingsPatch } from "../../api/paymentsSchemas";
import { useAuth, useCan } from "../../auth/AuthContext";
import { Button, Card, Chip, ConfirmDialog, EmptyState, ErrorState, Field, Loading, PageHeader, Segmented, TextInput } from "../../components/ui";
import { formatDateTime } from "../../lib/dates";
import {
  LIMITS,
  MODE_LABEL,
  PAYMENT_MODES,
  publishableKeyProblem,
  RESTRICTED_KEY_PERMISSIONS,
  secretKeyProblem,
  WEBHOOK_EVENTS,
  type PaymentMode,
} from "../../lib/payments";
import pages from "../pages.module.css";
import { TransactionsTable, WebhookEventsTable } from "./ActivitySection";
import { PaymentsError, ReadOnly, Switch, useDraft } from "./components";
import { PackagesSection } from "./PackagesSection";
import p from "./payments.module.css";

export function WebPaymentsPage() {
  const canView = useCan("payments.view");
  const canConfigure = useCan("payments.configure");
  const { admin } = useAuth();
  const q = useStripeSettings(canView);

  const header = (
    <PageHeader
      title="Web payments"
      meta={
        <span>
          Pro on iqdaily.app/play, sold through Stripe Checkout. Buying needs a signed-in account. Keys are stored encrypted; only their last 4 characters
          come back.
        </span>
      }
      actions={
        canConfigure ? (
          <span className="monoLabel">admin · every setting</span>
        ) : canView ? (
          <ReadOnly testId="payments-role-note">Read-only · you are {admin?.role}</ReadOnly>
        ) : null
      }
    />
  );

  if (!canView) {
    return (
      <>
        {header}
        <EmptyState title="Web payments need the editor role">You are signed in as a {admin?.role}. Ask an admin if you need access.</EmptyState>
      </>
    );
  }
  if (q.isPending)
    return (
      <>
        {header}
        <Loading rows={10} />
      </>
    );
  if (q.isError)
    return (
      <>
        {header}
        <ErrorState error={q.error} onRetry={() => void q.refetch()} />
      </>
    );

  const s = q.data;
  return (
    <>
      {header}
      <StatusBanner s={s} />
      <div className={p.grid}>
        {PAYMENT_MODES.map((mode) => (
          <ConnectionCard key={mode} mode={mode} conn={s[mode]} active={s.mode === mode} selling={s.enabled && s.mode === mode} canConfigure={canConfigure} />
        ))}
      </div>
      <div className={p.stack}>
        <RestrictedKeyHelp />
        <SettingsCard s={s} canConfigure={canConfigure} />
      </div>
      <Sections s={s} canConfigure={canConfigure} />
    </>
  );
}

// ---------------------------------------------------------------- status

function StatusBanner({ s }: { s: StripeSettings }) {
  const conn = s[s.mode];
  const [tone, title, text] = !s.enabled
    ? (["muted", "Web sales are off", "The web shows no plans. Turn Enabled on in Settings once the packages are ready."] as const)
    : !conn.connected
      ? (["warning", "Not selling", `Enabled is on, but ${s.mode} mode isn't connected, so the web shows no plans.`] as const)
      : s.mode === "test"
        ? (["accent", "Selling in test mode", "Checkout takes Stripe's test cards only. Switch to live to take real payments."] as const)
        : (["success", "Selling live", "Checkout charges real cards."] as const);
  return (
    <div className={p.status} data-tone={tone} role="status" data-testid="payments-status">
      <span className={p.statusDot} aria-hidden />
      <strong>{title}</strong>
      <span>{text}</span>
    </div>
  );
}

// ---------------------------------------------------------------- connection

const shortKey = (k: string) => (k.length > 22 ? `${k.slice(0, 14)}…${k.slice(-4)}` : k);

function ConnectionCard({ mode, conn, active, selling, canConfigure }: { mode: PaymentMode; conn: StripeConn; active: boolean; selling: boolean; canConfigure: boolean }) {
  const [replacing, setReplacing] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const disconnect = useDisconnectStripe();
  const showForm = canConfigure && (!conn.connected || replacing);
  return (
    <Card
      title={`${MODE_LABEL[mode]} mode`}
      actions={
        <>
          {active ? <Chip tone="accent">active mode</Chip> : null}
          <Chip tone={conn.connected ? "success" : "muted"}>{conn.connected ? "connected" : "not connected"}</Chip>
        </>
      }
    >
      <div data-testid={`conn-${mode}`}>
        {conn.connected ? (
          <dl className={p.kv}>
            <dt>Account</dt>
            <dd>
              <span>{conn.accountName ?? "—"}</span>
              {conn.accountId ? <span className={p.mono}>{conn.accountId}</span> : null}
            </dd>
            <dt>Publishable key</dt>
            <dd className={p.mono} title={conn.publishableKey ?? undefined}>
              {conn.publishableKey ? shortKey(conn.publishableKey) : "—"}
            </dd>
            <dt>Secret key</dt>
            <dd className={p.mono} data-testid={`secret-last4-${mode}`}>
              {conn.secretLast4 ? `••••${conn.secretLast4}` : "—"}
            </dd>
            <dt>Webhook</dt>
            <dd>
              {conn.webhook ? (
                <>
                  <span className={p.mono}>{conn.webhook.url ?? conn.webhook.id}</span>
                  <Chip tone={conn.webhook.status === "enabled" ? "success" : conn.webhook.status === "disabled" ? "danger" : "muted"}>{conn.webhook.status ?? "unknown"}</Chip>
                </>
              ) : (
                <Chip tone="warning">not set up</Chip>
              )}
            </dd>
            <dt>Connected</dt>
            <dd className={p.mono}>{conn.connectedAt ? formatDateTime(conn.connectedAt) : "—"}</dd>
            <dt>Last event</dt>
            <dd className={p.mono}>{conn.lastEventAt ? formatDateTime(conn.lastEventAt) : "none yet"}</dd>
          </dl>
        ) : (
          <p className={p.empty}>
            {canConfigure
              ? `Paste the ${mode} keys from the Stripe dashboard (Developers → API keys${mode === "test" ? ", with Test mode on" : ""}). The server checks them with Stripe and creates the webhook endpoint.`
              : `No ${mode} keys yet. An admin connects them.`}
          </p>
        )}
        {showForm ? (
          <KeysForm
            mode={mode}
            replacing={conn.connected}
            onDone={() => setReplacing(false)}
            onCancel={conn.connected ? () => setReplacing(false) : undefined}
          />
        ) : null}
        {canConfigure && conn.connected && !replacing ? (
          <div className={p.foot}>
            <Button size="sm" variant="secondary" onClick={() => setReplacing(true)} data-testid={`replace-${mode}`}>
              Replace keys
            </Button>
            <Button
              size="sm"
              variant="danger"
              onClick={() => {
                disconnect.reset();
                setDisconnecting(true);
              }}
              data-testid={`disconnect-${mode}`}
            >
              Disconnect
            </Button>
          </div>
        ) : null}
        {!canConfigure ? (
          <div className={p.foot}>
            <ReadOnly>Read-only · keys need the admin role</ReadOnly>
          </div>
        ) : null}
      </div>
      <ConfirmDialog
        open={disconnecting}
        title={`Disconnect ${mode} mode?`}
        confirmLabel="Disconnect"
        tone="danger"
        busy={disconnect.isPending}
        onCancel={() => setDisconnecting(false)}
        onConfirm={() => disconnect.mutate(mode, { onSuccess: () => setDisconnecting(false) })}
      >
        <p className={pages.prose}>
          The webhook endpoint is deleted in Stripe and both keys are forgotten.{" "}
          {selling ? "This is the mode the web sells in: checkout stops until you connect again." : "Packages stay, and existing subscriptions keep running in Stripe."}
        </p>
        <PaymentsError error={disconnect.error} />
      </ConfirmDialog>
    </Card>
  );
}

function KeysForm({ mode, replacing, onDone, onCancel }: { mode: PaymentMode; replacing: boolean; onDone: () => void; onCancel?: () => void }) {
  const [secret, setSecret] = useState("");
  const [publishable, setPublishable] = useState("");
  const connect = useConnectStripe();
  const secretProblem = secretKeyProblem(mode, secret);
  const publishableProblem = publishableKeyProblem(mode, publishable);
  const label = MODE_LABEL[mode];
  return (
    <form
      className={p.keysForm}
      onSubmit={(e) => {
        e.preventDefault();
        if (secretProblem || publishableProblem) return;
        connect.mutate(
          { mode, secretKey: secret.trim(), publishableKey: publishable.trim() },
          {
            onSuccess: () => {
              setSecret("");
              setPublishable("");
              onDone();
            },
          },
        );
      }}
      data-testid={`keys-form-${mode}`}
    >
      <Field label="Secret key" hint={`sk_${mode}_… or a restricted rk_${mode}_… key. Never shown again.`}>
        <TextInput
          type="password"
          autoComplete="off"
          spellCheck={false}
          value={secret}
          placeholder={`sk_${mode}_…`}
          onChange={(e) => setSecret(e.target.value)}
          aria-label={`${label} secret key`}
          aria-invalid={secret && secretProblem ? true : undefined}
          className={p.mono}
          data-testid={`secret-${mode}`}
        />
        {secret && secretProblem ? <span className={pages.fieldError}>{secretProblem}</span> : null}
      </Field>
      <Field label="Publishable key" hint={`pk_${mode}_…, from the same page in Stripe.`}>
        <TextInput
          autoComplete="off"
          spellCheck={false}
          value={publishable}
          placeholder={`pk_${mode}_…`}
          onChange={(e) => setPublishable(e.target.value)}
          aria-label={`${label} publishable key`}
          aria-invalid={publishable && publishableProblem ? true : undefined}
          className={p.mono}
          data-testid={`publishable-${mode}`}
        />
        {publishable && publishableProblem ? <span className={pages.fieldError}>{publishableProblem}</span> : null}
      </Field>
      <PaymentsError error={connect.error} context="keys" testId={`connect-error-${mode}`} />
      <div className={p.foot}>
        {onCancel ? (
          <Button
            size="sm"
            variant="ghost"
            onClick={() => {
              connect.reset();
              onCancel();
            }}
          >
            Cancel
          </Button>
        ) : null}
        <Button type="submit" size="sm" variant="primary" busy={connect.isPending} disabled={Boolean(secretProblem || publishableProblem)} data-testid={`connect-${mode}`}>
          {replacing ? "Save new keys" : `Connect ${mode} mode`}
        </Button>
      </div>
    </form>
  );
}

function RestrictedKeyHelp() {
  return (
    <details className={p.help} data-testid="restricted-key-help">
      <summary>Using a restricted key?</summary>
      <div className={p.helpBody}>
        <p>
          A restricted key (<code>rk_…</code>) works if it has these permissions. Create it in Stripe under Developers → API keys → Create restricted key.
        </p>
        <ul className={p.perms} aria-label="Restricted key permissions">
          {RESTRICTED_KEY_PERMISSIONS.map((perm) => (
            <li key={perm.resource}>
              <span>{perm.resource}</span>
              <Chip tone={perm.access === "Write" ? "accent" : "info"}>{perm.access.toLowerCase()}</Chip>
            </li>
          ))}
        </ul>
        <p>
          The webhook endpoint is created for you. It listens to {WEBHOOK_EVENTS.length} events: <code>{WEBHOOK_EVENTS.join(", ")}</code>.
        </p>
      </div>
    </details>
  );
}

// ---------------------------------------------------------------- settings

interface Pending {
  title: string;
  body: string;
  confirmLabel: string;
  tone: "primary" | "danger";
  patch: StripeSettingsPatch;
}

function SettingsCard({ s, canConfigure }: { s: StripeSettings; canConfigure: boolean }) {
  const confirmed = useUpdateStripeSettings();
  const options = useUpdateStripeSettings();
  const [pending, setPending] = useState<Pending | null>(null);
  const server = { allowPromotionCodes: s.allowPromotionCodes, automaticTax: s.automaticTax, trialOncePerUser: s.trialOncePerUser, graceDays: String(s.graceDays) };
  const [draft, setDraft, dirty] = useDraft(server);
  const grace = Number(draft.graceDays);
  const graceOk = draft.graceDays.trim() !== "" && Number.isInteger(grace) && grace >= 0 && grace <= LIMITS.graceDays;
  const ask = (next: Pending) => {
    confirmed.reset();
    setPending(next);
  };

  const switchMode = (mode: PaymentMode) =>
    mode !== s.mode &&
    ask(
      mode === "live"
        ? {
            title: "Switch to live mode?",
            body: `Checkout starts charging real cards with the live packages${s.enabled ? "" : " once Enabled is on"}. Test purchases stay in test mode.`,
            confirmLabel: "Switch to live",
            tone: "danger",
            patch: { mode },
          }
        : {
            title: "Switch to test mode?",
            body: "Checkout goes back to Stripe's test cards. Live subscriptions keep renewing in Stripe, but nobody can buy live until you switch back.",
            confirmLabel: "Switch to test",
            tone: "primary",
            patch: { mode },
          },
    );

  const changed: StripeSettingsPatch = {};
  if (draft.allowPromotionCodes !== s.allowPromotionCodes) changed.allowPromotionCodes = draft.allowPromotionCodes;
  if (draft.automaticTax !== s.automaticTax) changed.automaticTax = draft.automaticTax;
  if (draft.trialOncePerUser !== s.trialOncePerUser) changed.trialOncePerUser = draft.trialOncePerUser;
  if (graceOk && grace !== s.graceDays) changed.graceDays = grace;

  return (
    <Card title="Settings" actions={<span className="monoLabel">payments.stripe</span>}>
      <div className={p.settings} data-testid="payments-settings">
        <div className={p.setting}>
          <Segmented
            label="Active mode"
            value={s.mode}
            options={PAYMENT_MODES.map((m) => ({
              value: m,
              label: MODE_LABEL[m],
              disabled: m !== s.mode && !s[m].connected,
              title: m !== s.mode && !s[m].connected ? `Connect ${m} mode first` : undefined,
            }))}
            disabled={!canConfigure}
            onChange={switchMode}
          />
          <div className={p.settingText}>
            <strong>Active mode: {MODE_LABEL[s.mode].toLowerCase()}</strong>
            <p>{s[s.mode === "test" ? "live" : "test"].connected ? "The mode the web sells in. Switching asks first." : `Connect ${s.mode === "test" ? "live" : "test"} mode to switch to it.`}</p>
          </div>
        </div>
        <div className={p.setting}>
          <Switch
            checked={s.enabled}
            label="Enabled (sell Pro on the web)"
            disabled={!canConfigure}
            testId="payments-enabled"
            onClick={() =>
              ask(
                s.enabled
                  ? {
                      title: "Stop selling on the web?",
                      body: "The web hides its plans. Existing subscriptions and Lifetime purchases keep working.",
                      confirmLabel: "Turn off",
                      tone: "danger",
                      patch: { enabled: false },
                    }
                  : {
                      title: `Start selling in ${s.mode} mode?`,
                      body:
                        s.mode === "live"
                          ? "Signed-in web players see the live packages and are charged real money."
                          : "Signed-in web players see the test packages; checkout takes Stripe's test cards only.",
                      confirmLabel: "Turn on",
                      tone: s.mode === "live" ? "danger" : "primary",
                      patch: { enabled: true },
                    },
              )
            }
          />
          <div className={p.settingText}>
            <strong>Enabled: {s.enabled ? "on" : "off"}</strong>
            <p>The master switch. The web shows plans only while it's on and the active mode is connected.</p>
            {s.enabled && !s[s.mode].connected ? <p className={p.warn}>The active mode isn&rsquo;t connected.</p> : null}
          </div>
        </div>
        <div className={p.setting}>
          <Switch
            checked={draft.allowPromotionCodes}
            label="Allow promotion codes"
            disabled={!canConfigure}
            testId="payments-promo"
            onClick={() => setDraft({ ...draft, allowPromotionCodes: !draft.allowPromotionCodes })}
          />
          <div className={p.settingText}>
            <strong>Promotion codes</strong>
            <p>Checkout shows a code field. Codes are made in Stripe (Products → Coupons).</p>
          </div>
        </div>
        <div className={p.setting}>
          <Switch
            checked={draft.automaticTax}
            label="Automatic tax"
            disabled={!canConfigure}
            testId="payments-tax"
            onClick={() => setDraft({ ...draft, automaticTax: !draft.automaticTax })}
          />
          <div className={p.settingText}>
            <strong>Automatic tax</strong>
            <p>Stripe Tax adds tax at checkout.</p>
            <p className={p.warn}>Needs Stripe Tax set up in the Stripe dashboard first; otherwise checkout fails.</p>
          </div>
        </div>
        <div className={p.setting}>
          <Switch
            checked={draft.trialOncePerUser}
            label="Trial once per player"
            disabled={!canConfigure}
            testId="payments-trial-once"
            onClick={() => setDraft({ ...draft, trialOncePerUser: !draft.trialOncePerUser })}
          />
          <div className={p.settingText}>
            <strong>Trial once per player</strong>
            <p>Only players who never had Pro, from any source, get a package&rsquo;s trial.</p>
          </div>
        </div>
        <div className={p.setting}>
          <div className={p.inline}>
            <TextInput
              inputMode="numeric"
              value={draft.graceDays}
              onChange={(e) => setDraft({ ...draft, graceDays: e.target.value })}
              readOnly={!canConfigure}
              aria-label="Grace days"
              aria-invalid={!graceOk || undefined}
              className={p.number}
              data-testid="payments-grace"
            />
            <span className={p.unit}>days</span>
          </div>
          <div className={p.settingText}>
            <strong>Grace</strong>
            <p>How long Pro lasts after a renewal fails, while Stripe retries the card.</p>
            {!graceOk ? <p className={pages.fieldError}>A whole number from 0 to {LIMITS.graceDays}.</p> : null}
          </div>
        </div>
      </div>
      <PaymentsError error={options.error} context="settings" />
      {canConfigure ? (
        dirty ? (
          <div className={p.foot}>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                options.reset();
                setDraft(server);
              }}
            >
              Undo
            </Button>
            <Button size="sm" variant="primary" busy={options.isPending} disabled={!graceOk || Object.keys(changed).length === 0} onClick={() => options.mutate(changed)} data-testid="save-payment-settings">
              Save settings
            </Button>
          </div>
        ) : null
      ) : (
        <div className={p.foot}>
          <ReadOnly testId="settings-read-only">Read-only · settings need the admin role</ReadOnly>
        </div>
      )}
      <ConfirmDialog
        open={pending !== null}
        title={pending?.title ?? ""}
        confirmLabel={pending?.confirmLabel ?? "Save"}
        tone={pending?.tone}
        busy={confirmed.isPending}
        onCancel={() => setPending(null)}
        onConfirm={() => pending && confirmed.mutate(pending.patch, { onSuccess: () => setPending(null) })}
      >
        <p className={pages.prose}>{pending?.body}</p>
        <p className={pages.faint}>Written to the audit log.</p>
        <PaymentsError error={confirmed.error} context="settings" />
      </ConfirmDialog>
    </Card>
  );
}

// ---------------------------------------------------------------- packages, transactions, events

const TABS = [
  { value: "packages", label: "Packages" },
  { value: "transactions", label: "Transactions" },
  { value: "events", label: "Webhook events" },
] as const;
type Tab = (typeof TABS)[number]["value"];

function Sections({ s, canConfigure }: { s: StripeSettings; canConfigure: boolean }) {
  const [tab, setTab] = useState<Tab>("packages");
  const [mode, setMode] = useState<PaymentMode>(s.mode);
  const id = useId();
  return (
    <section className={p.sections} aria-label="Packages and activity">
      <div className={p.sectionsHead}>
        <div className={p.tabs} role="tablist" aria-label="Web payments sections">
          {TABS.map((t) => (
            <button
              key={t.value}
              type="button"
              role="tab"
              id={`${id}-${t.value}`}
              aria-selected={tab === t.value}
              aria-controls={`${id}-panel`}
              className={p.tab}
              onClick={() => setTab(t.value)}
            >
              {t.label}
            </button>
          ))}
        </div>
        <Segmented
          label="Mode shown"
          value={mode}
          options={PAYMENT_MODES.map((m) => ({ value: m, label: m === s.mode ? `${MODE_LABEL[m]} · active` : MODE_LABEL[m] }))}
          onChange={setMode}
        />
      </div>
      <div role="tabpanel" id={`${id}-panel`} aria-labelledby={`${id}-${tab}`}>
        {tab === "packages" ? (
          <PackagesSection mode={mode} connected={s[mode].connected} canConfigure={canConfigure} />
        ) : tab === "transactions" ? (
          <TransactionsTable mode={mode} />
        ) : (
          <WebhookEventsTable mode={mode} />
        )}
      </div>
    </section>
  );
}
